import type { IRatingMatrixFrameworkSubParameter } from '__generated__';
import {
  baseApi,
  parameterListTag,
  subParameterListTag,
  templateDetailTag,
} from 'store/api';
import {
  API,
  APIRead,
  asPatchBody,
  getAllDynamic,
  normalizeError,
  type PatchBody,
} from 'utils/api-client';
import { buildSubParameterFilter } from './helpers';

export const ratingMatrixFrameworkSubParameterApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubParameters: builder.query<
      IRatingMatrixFrameworkSubParameter[],
      { parameterID: number; frameworkTemplateID: number }
    >({
      async queryFn({ parameterID }) {
        try {
          const { value } = await getAllDynamic<IRatingMatrixFrameworkSubParameter>(
            (body) => APIRead.api.ratingMatrixFrameworkSubParameterGetAllDynamicCreate(body),
            {
              filter: buildSubParameterFilter(parameterID),
              orderBy: 'groupSortOrder asc, sortOrder asc',
            },
          );

          return { data: value };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [subParameterListTag(arg.frameworkTemplateID)],
    }),

    createSubParameter: builder.mutation<
      IRatingMatrixFrameworkSubParameter,
      Partial<IRatingMatrixFrameworkSubParameter> & {
        frameworkTemplateID: number;
      }
    >({
      async queryFn(body) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkSubParameterCreateCreate(body);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        subParameterListTag(arg.frameworkTemplateID),
        templateDetailTag(arg.frameworkTemplateID),
      ],
    }),

    patchSubParameter: builder.mutation<
      string,
      {
        id: number;
        frameworkTemplateID: number;
        patch: PatchBody<IRatingMatrixFrameworkSubParameter>;
      }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkSubParameterPatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        subParameterListTag(arg.frameworkTemplateID),
        templateDetailTag(arg.frameworkTemplateID),
      ],
    }),

    deleteSubParameter: builder.mutation<
      IRatingMatrixFrameworkSubParameter,
      { id: number; frameworkTemplateID: number }
    >({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkSubParameterDeleteDelete({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        subParameterListTag(arg.frameworkTemplateID),
        templateDetailTag(arg.frameworkTemplateID),
      ],
    }),
  }),
});

export const {
  useGetSubParametersQuery,
  useCreateSubParameterMutation,
  usePatchSubParameterMutation,
  useDeleteSubParameterMutation,
} = ratingMatrixFrameworkSubParameterApi;
